import { TFunction } from 'i18next';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { ErrorMessageKey, ErrorPropertyKey } from '@/entities';

/**
 * Get localized error message from Redux error
 * @param error - Redux error object
 * @param t - Translation function from useTranslation('errors')
 * @returns Localized error message
 */
export const getLocalizedReduxErrorMessage = (
  error: FetchBaseQueryError | SerializedError,
  t: TFunction<'errors', undefined>,
): string => {
  if ('status' in error) {
    const errorData = error.data as {
      code: string;
      message: string;
      statusCode: number;
      details?: { errors: [{ property: string; messages: string[] }] };
    };

    let messageText: string = '';

    if (errorData && errorData.code) {
      // Try to get localized message using the new key system
      const messageKey = ErrorMessageKey[errorData.code as keyof typeof ErrorMessageKey];
      if (messageKey) messageText = t(messageKey);
      else messageText = errorData.code; // Fallback to the error code itself

      // Handle field validation errors
      if (errorData.details?.errors && errorData.details.errors.length > 0) {
        const fieldLabel = errorData.details.errors.length === 1 ? t('labels.fieldSingle') : t('labels.fieldMultiple');

        const fieldNames = errorData.details.errors
          .map((e) => {
            const propertyKey = ErrorPropertyKey[e.property as keyof typeof ErrorPropertyKey];
            return propertyKey ? t(propertyKey) : e.property;
          })
          .join(', ');

        messageText += ` ${fieldLabel} ${fieldNames}`;
      }
    } else messageText = 'error' in error ? error.error || t('messages.INTERNAL_ERROR') : t('messages.INTERNAL_ERROR');

    return messageText;
  } else if (error instanceof TypeError && error.message === 'Failed to fetch') {
    return t('messages.FETCH_FAILED');
  } else {
    return error.message || t('messages.INTERNAL_ERROR');
  }
};
